import ExcelJS from 'exceljs';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Download Valid Scans by Score Report with additional columns
 * Columns: UniqueToken, TestTakerLastName, TestTakerFirstName, DateScanned, CountryScanned, EPA, Product, TestDate
 * @param {Object} params
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} params.frequency - Frequency (weekly or monthly)
 * @returns {Promise<void>}
 */
export const downloadValidScansByScore = async (params) => {
  try {
    // Fetch data from your backend API (valid scans)
    const response = await fetch(`${API_BASE_URL}/api/v1/logs/valid-scans-by-score?start_date=${params.startDate}&end_date=${params.endDate}`,
      { credentials: 'include' }
    );
    const data = await response.json();

    // Check for authentication error
    if (data.error === 'Authentication required') {
      window.location.href = '/analytics';
      return; 
    }

    // Check if the response is successful
    if (data.success) {
      const validScansData = data.data;

      // Create a new Excel workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Valid Scans Report');

      // Add columns to the worksheet (These will be your headers)
      worksheet.columns = [
        { header: 'Unique Token', key: 'uniqueToken', width: 25 },
        { header: 'Test Taker Last Name', key: 'testTakerLastName', width: 20 },
        { header: 'Test Taker First Name', key: 'testTakerFirstName', width: 20 },
        { header: 'Date Scanned', key: 'dateScanned', width: 15 },
        { header: 'Country Scanned', key: 'countryScanned', width: 20 },
        { header: 'EPA Code', key: 'epaCode', width: 20 },
        { header: 'EPA Name', key: 'epaName', width: 20 },
        { header: 'Product', key: 'product', width: 20 },
        { header: 'Test Type', key: 'testType', width: 20 },
        { header: 'Test Date', key: 'testDate', width: 15 },
      ];

      // Prepare data for the Excel sheet with the required fields
      const rows = validScansData.map((item) => ({
        uniqueToken: item.UniqueToken,
        testTakerLastName: item.TestTakerLastName,
        testTakerFirstName: item.TestTakerFirstName,
        dateScanned: item.DateScanned,
        countryScanned: item.CountryScanned,
        epaCode: item.EPACode,
        epaName: item.EPAName,
        product: item.Product,
        testType: item.TestType,
        testDate: item.TestDate,
      }));

      // Add rows to the worksheet
      worksheet.addRows(rows);

      // Generate the Excel file and trigger the download
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Valid_Scans_Report_by_score_${params.startDate}_to_${params.endDate}.xlsx`;
        link.click();
      });
    } else {
      // Handle error if data retrieval fails
      console.error('Failed to fetch valid scans data');
    }
  } catch (error) {
    console.error('Error generating the report:', error);
  }
};

/**
 * Download Invalid Scans by Country Report
 * Columns: Date Scanned, Country Scanned
 * @param {Object} params
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} params.frequency - Frequency (weekly or monthly)
 * @returns {Promise<void>}
 */
export const downloadInvalidScansByCountry = async (params) => {
  try {
    // Fetch data from your backend API (invalid scans)
    const response = await fetch(`${API_BASE_URL}/api/v1/logs/invalid-scans?start_date=${params.startDate}&end_date=${params.endDate}`,
      { credentials: 'include' }
    );
    const data = await response.json();

    // Check for authentication error
    if (data.error === 'Authentication required') {
      window.location.href = '/analytics';
      return; 
    }
    
    // Check if the response is successful
    if (data.success) {
      const invalidScansData = data.data;

      // Create a new Excel workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Invalid Scans Report');

      // Add columns to the worksheet (These will be your headers)
      worksheet.columns = [
        { header: 'Date Scanned', key: 'dateScanned', width: 15 },
        { header: 'Country Scanned', key: 'countryScanned', width: 20 },
        { header: 'Failure Reason', key: 'reason', width: 20 },
      ];

      // Prepare data for the Excel sheet with only the required fields
      const rows = invalidScansData.map((item) => ({
        dateScanned: item.DateScanned,
        countryScanned: item.CountryScanned,
        reason: item.FailureReason
      }));

      // Add rows to the worksheet
      worksheet.addRows(rows);

      // Generate the Excel file and trigger the download
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Invalid_Scans_Report_${params.startDate}_to_${params.endDate}.xlsx`;
        link.click();
      });
    } else {
      // Handle error if data retrieval fails
      console.error('Failed to fetch invalid scans data');
    }
  } catch (error) {
    console.error('Error generating the report:', error);
  }
};

/**
 * Download Valid Scans by Score Report with additional columns
 * Columns: UniqueToken, TestTakerLastName, TestTakerFirstName, DateScanned, CountryScanned, EPA, Product, TestDate
 * @param {Object} params
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} params.frequency - Frequency (weekly or monthly)
 * @returns {Promise<void>}
 */
export const downloadUploadLogsReport = async (params) => {
  try {
    // Fetch data from your backend API (valid scans)
    const response = await fetch(`${API_BASE_URL}/api/v1/upload_logs/uploads-by-date?start_date=${params.startDate}&end_date=${params.endDate}`,
      { credentials: 'include' }
    );
    const data = await response.json();

    // Check for authentication error
    if (data.error === 'Authentication required') {
      window.location.href = '/analytics';
      return; 
    }

    // Check if the response is successful
    if (data.success) {
      const uploadScansData = data.data;

      // Create a new Excel workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('S3 Data Upload');

      // Add columns to the worksheet (These will be your headers)
      worksheet.columns = [
        { header: 'EPN Code', key: 'EPNCode', width: 12 },
        { header: 'Total Requested', key: 'TotalRequested', width: 15 },
        { header: 'Successful Uploads', key: 'SuccessfulUploads', width: 20 },
        { header: 'Failed Uploads', key: 'FailedUploads', width: 15 },
        { header: 'Date Uploaded', key: 'DateUploaded', width: 15 },
        { header: 'Time Uploaded', key: 'TimeUploaded', width: 15 },
      ];

      // Prepare data for the Excel sheet with the required fields
      const rows = uploadScansData.map((item) => ({
        EPNCode: item.EPNCode,
        TotalRequested: item.TotalRequested,
        SuccessfulUploads: item.SuccessfulUploads,
        FailedUploads: item.FailedUploads,
        DateUploaded: item.DateUploaded,
        TimeUploaded: item.TimeUploaded
      }));

      // Add rows to the worksheet
      worksheet.addRows(rows);

      // Generate the Excel file and trigger the download
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Upload_to_S3_Report_${params.startDate}_to_${params.endDate}.xlsx`;
        link.click();
      });
    } else {
      // Handle error if data retrieval fails
      console.error('Failed to fetch uploads data');
    }
  } catch (error) {
    console.error('Error generating the report:', error);
  }
};

const reportApi = {
  downloadValidScansByScore,
  downloadInvalidScansByCountry,
  downloadUploadLogsReport
};

export default reportApi;